import * as offset from 'document-offset';
import * as React from 'react';
import { Portal } from 'semantic-ui-react';

import ErrorCatch from '../../containers/ErrorBoundary/ErrorCatch';
import { addModifiers } from '../../shared/helpers';

import './SelectPortal.scss';

interface SelectPortalProps {
  options: any[];
  handle: (data: string) => void;
  id: string;
  value: string;
  placeholder: string;
  className?: string;
  menuModifiers?: string[];
  // For the dropdown follow the scroll of a element needs to pass a class starting by a dot
  classNameOnScroll?: string;
  menuTopHeight?: number;
  optionsSize?: number;
}

interface SelectPortalStatic {
  portalLeft: number;
  portalTop: number;
  portalWidth: number;
  isOpen: boolean;
  isMenuUp: boolean;
  windowIsResizing: number;
}
interface SelectListProps {
  options: any[];
  handle: (data: string) => void;
  id: any;
  value: string;
  portalLeft: number;
  portalTop: number;
  portalWidth: number;
  isMenuUp?: boolean;
  menuModifiers?: string[];
}

const SelectPortalList = ({
  menuModifiers = [],
  options,
  handle,
  id,
  value,
  portalLeft,
  portalTop,
  portalWidth,
  isMenuUp,
}: SelectListProps) => {
  try {
    return (
      <div
        className={addModifiers('select-portal__content', isMenuUp ? menuModifiers.concat(['up']) : menuModifiers)}
        style={{ left: `${portalLeft}px`, position: 'absolute', top: `${portalTop}px`, minWidth: `${portalWidth}px` }}
      >
        <ul className="select-portal__list">
          {options.map((option: any) => {
            const handleClick = (e: any) => {
              e.stopPropagation();
              handle(option.value);
            };

            return (
              <li
                key={`${id}-${option.key}`}
                onClick={handleClick}
                className={addModifiers('select-portal__item', value === option.value ? ['selected'] : [])}
              >
                {option.text}
              </li>
            );
          })}
        </ul>
      </div>
    );
  } catch (error) {
    return (
      <div
        className="select-portal__content select-portal__content--errors"
        style={{ left: `${portalLeft}px`, position: 'absolute', top: `${portalTop}px`, minWidth: `${portalWidth}px` }}
      >
        <ErrorCatch error={error} />
      </div>
    );
  }
};

export class SelectPortal extends React.Component<SelectPortalProps, SelectPortalStatic> {
  private selectPortalRef: any;

  constructor(props: SelectPortalProps) {
    super(props);
    this.state = {
      isOpen: false,
      portalLeft: 0,
      portalTop: 0,
      portalWidth: 0,
      isMenuUp: false,
      windowIsResizing: 0,
    };
  }

  handleOpen = () => {
    this.setPosition(true);
  };

  scroll = () => {
    this.setState({ isOpen: false });
  };

  updateMenuDirection = () => {
    const element: any = document.getElementById(this.props.id);
    if (element) {
      const offSet: number = element.offsetTop;
      const optionsLength: number = this.props.options.length;
      const menuTopHeight = this.props.menuTopHeight ?? 0;
      const optionsSize = this.props.optionsSize ?? 0;
      const menuHeight = this.props.options ? offSet + menuTopHeight + (optionsLength + 2) * optionsSize : 0;
      if (menuHeight > window.innerHeight) {
        this.setState({ isMenuUp: true });
      } else {
        this.setState({ isMenuUp: false });
      }
    }
  };

  componentDidUpdate(_: any, prevState: any) {
    if (prevState.windowIsResizing !== this.state.windowIsResizing) {
      this.updateMenuDirection();
    }
  }

  setPositionDown(isOpen: boolean) {
    const portalTopOffset = offset(this.selectPortalRef);
    const top: number = portalTopOffset.top;
    const portalHeight: number = this.selectPortalRef.offsetHeight;
    const portalTop = top + portalHeight;
    this.setState({
      isOpen,
      portalLeft: portalTopOffset.left,
      portalTop,
      portalWidth: this.selectPortalRef.offsetWidth,
    });
  }

  setPositionUp(isOpen: boolean) {
    const portalTopOffset = offset(this.selectPortalRef);
    const top: number = portalTopOffset.top;
    // Size of the dropdown menu equals to number of options multiply by the size of each plus paddings
    const optionsSize = this.props.optionsSize ? this.props.optionsSize : 0;
    const sizeMenu = this.props.options ? this.props.options.length * optionsSize + 12 : 0;
    const portalTop = top - (sizeMenu ? sizeMenu : 0);

    this.setState({
      isOpen,
      portalLeft: portalTopOffset.left,
      portalTop,
      portalWidth: this.selectPortalRef.offsetWidth,
    });
  }

  setPosition = (isOpen: boolean) => {
    if (this.state.isMenuUp) {
      this.setPositionUp(isOpen);
    } else {
      this.setPositionDown(isOpen);
    }
  };

  handleSelectPortalRef = (ref: any) => {
    this.selectPortalRef = ref;
  };

  handleClose = () => {
    this.setState({ isOpen: false });
  };

  componentDidMount() {
    window.addEventListener('resize', this.resize);
    this.updateMenuDirection();
    if (this.props.classNameOnScroll) {
      const element: any = document.querySelector(this.props.classNameOnScroll);
      if (element) {
        element.addEventListener('scroll', this.scroll);
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resize);
    if (this.props.classNameOnScroll) {
      const element: any = document.querySelector(this.props.classNameOnScroll);
      if (element) {
        element.removeEventListener('scroll', this.scroll);
      }
    }
  }

  resize = () => {
    this.setState(state => ({ windowIsResizing: state.windowIsResizing + 1, isOpen: false }));
  };

  handleClick = (data: string) => {
    this.setState({ isOpen: false }, () => this.props.handle(data));
  };

  render() {
    const { portalLeft, portalTop, portalWidth } = this.state;
    let placeholderModifier = ['placeholder'];
    let btnContent = this.props.placeholder;

    const value = this.props.value;
    if (value !== '' && value != null) {
      placeholderModifier = [];
      const label: any = this.props.options.filter((option: any) => option.value.toString() === value.toString());
      btnContent = label.length > 0 ? label[0].text : btnContent;
    }

    return (
      <Portal
        closeOnTriggerClick={true}
        openOnTriggerClick={true}
        trigger={
          <button
            ref={this.handleSelectPortalRef}
            className={this.props.className ? this.props.className : addModifiers('select-portal__btn', placeholderModifier)}
            id={this.props.id}
          >
            {btnContent}
          </button>
        }
        open={this.state.isOpen}
        onOpen={this.handleOpen}
        onClose={this.handleClose}
      >
        <SelectPortalList
          handle={this.handleClick}
          options={this.props.options}
          id={this.props.id}
          value={value}
          portalLeft={portalLeft}
          portalTop={portalTop}
          portalWidth={portalWidth}
          isMenuUp={this.state.isMenuUp}
          menuModifiers={this.props.menuModifiers}
        />
      </Portal>
    );
  }
}
